import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Input, Select, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';

//* COMPONENTS
import { locationTypeIcon } from 'common/components/icon-renderer/IconRendererMapping';
import {
  Form,
  CustomNotification,
  StyledModal,
  WrapperSelect,
  FlagCountrySelectionFieldInput,
} from 'common/components';

import { useInvalidateMemberHeader } from 'common/queries/member-module';

//* SERVICES
import * as memberServices from 'services/members';
import Messages from 'i18n/messages/message-my-company';

//* UTILS
import { searchCountryCode } from 'utils/country';
import CountryCode from 'static/CountryCode';

import CityAutocomplete from 'common/components/google-map/CityAutocomplete';
import StateAutocomplete from 'common/components/google-map/StateAutocomplete';
import './CompanyLocationForm.less';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const CompanyLocationForm = (props) => {
  const {
    isOpen,
    isAddNew,
    setIsOpen,
    itemData,
    intl,
    gridApi,
    memberId,
    type,
    onSubmit,
  } = props;

  const [formInstance] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const { invalidateMemberHeader } = useInvalidateMemberHeader(memberId);

  const refCountry = useRef(null);

  const handleSubmitForm = async () => {
    try {
      const formValue = await formInstance.validateFields();

      if (onSubmit) {
        return onSubmit({ formValue, setIsLoading });
      }

      setIsLoading(true);

      const response = isAddNew
        ? await memberServices.addMyCompanyLocation({
            memberId,
            ...formValue,
          })
        : await memberServices.updateMyCompanyLocation({
            id: itemData?.id,
            memberId,
            ...formValue,
          });

      setIsLoading(false);

      if (response?.isSuccess) {
        CustomNotification.success(
          intl.formatMessage(
            isAddNew
              ? Messages.msgLocationCreateSuccess
              : Messages.msgLocationUpdateSuccess
          )
        );
        gridApi?.rivirPurgeServerSideCache();
        invalidateMemberHeader();
        setIsOpen(false);
      } else {
        CustomNotification.error(
          response?.message ||
            intl.formatMessage(Messages.msgLocationUpdateFail)
        );
      }
    } catch (error) {
      setIsLoading(false);

      if (error?.errorFields) {
        formInstance.scrollToField(error?.errorFields[0].name[0]);
        CustomNotification.error(intl.formatMessage(Messages.msgInvalidFields));
      } else {
        CustomNotification.error(intl.formatMessage(Messages.msgExcepEdit));
      }
    }
  };

  useEffect(() => {
    //* set form Data to edit
    const setInitFormDataForEdit = async () => {
      if (isOpen && itemData?.id && !isAddNew) {
        setIsLoading(true);
        const response = await memberServices.getCompanyLocationDetail(
          itemData?.id
        );
        setIsLoading(false);

        if (response?.isSuccess) {
          formInstance.setFieldsValue(response?.data);
          refCountry &&
            refCountry.current &&
            refCountry.current.updateSelected(
              searchCountryCode(response.data?.country)
            );
        } else {
          CustomNotification.error('Failed to get contact detail info');
        }
      }
    };

    setInitFormDataForEdit();
  }, [isOpen, itemData, isAddNew]);

  return (
    <React.Fragment>
      <StyledModal
        className={classnames('company-location-form__wrapper', {
          'company-location-form__wrapper--loading': isLoading,
        })}
        title={
          isAddNew ? (
            <FormattedMessage {...Messages.formLocationTitleNew} />
          ) : (
            <FormattedMessage {...Messages.formLocationTitle} />
          )
        }
        visible={isOpen || isAddNew}
        onOk={handleSubmitForm}
        onCancel={() => setIsOpen(false)}
        okText={
          isLoading ? (
            <React.Fragment>
              <LoadingOutlined />
              <Text style={{ color: 'white' }}>
                <FormattedMessage {...Messages.btnSending} />
              </Text>
            </React.Fragment>
          ) : (
            <FormattedMessage {...Messages.btnSubmit} />
          )
        }
        cancelText={<FormattedMessage {...Messages.btnCancel} />}
        maskClosable={false}
        destroyOnClose
      >
        <CompanyLocationFormBody
          formInstance={formInstance}
          isLoading={isLoading}
          type={type}
          refCountry={refCountry}
        />
      </StyledModal>
    </React.Fragment>
  );
};

/**
 *  ! SUB COMPONENT - company location form content
 * @param {*} props
 * @returns
 */
const CompanyLocationFormBody = injectIntl((props) => {
  const { formInstance, intl, refCountry, type } = props;

  useEffect(() => {
    return () => {
      formInstance.resetFields();
      formInstance.setFieldsValue({
        addressType: type ? type : null,
        country: CountryCode['US'],
      });
    };
  }, []);

  useEffect(() => {
    formInstance.setFieldsValue({
      addressType: type ? type : null,
    });
  }, [type]);

  const country = Form.useWatch('country', formInstance);

  return (
    <Form
      form={formInstance}
      className='company-location-form'
      initialValues={{
        status: 'Active',
        country: CountryCode['US'],
      }}
      layout='vertical'
      scrollToFirstError={true}
      id='company-location-edit-form'
    >
      <Form.Item
        name='addressType'
        label={<FormattedMessage {...Messages.labelType} />}
        rules={[
          {
            required: true,
            message: 'Type is required',
          },
        ]}
      >
        <WrapperSelect
          getPopupContainer={() =>
            document.getElementById('company-location-edit-form')
          }
          placeholder={intl.formatMessage(Messages.placeHolderLocationType)}
          disabled={!!type}
        >
          {locationTypeIcon.map((locationIconItem) => (
            <Option
              className={classnames(
                'company-location-form__select-option',
                'company-location-form__select-option--with-icon'
              )}
              value={locationIconItem.value}
              key={locationIconItem.value}
            >
              <locationIconItem.icon />
              {locationIconItem.label}
            </Option>
          ))}
        </WrapperSelect>
      </Form.Item>
      <Form.Item
        name='mainAddress'
        label={<FormattedMessage {...Messages.labelAddress} />}
        rules={[
          {
            required: true,
            message: 'Address is required',
          },
        ]}
      >
        <CityAutocomplete
          placeholder={intl.formatMessage(Messages.placeHolderAddress)}
          country={country}
          types={['address']}
          onCityStateSelect={({ city, state, zipcode, lat, lng }) => {
            formInstance.setFieldsValue({
              city,
              state,
              zipcode,
              latitude: lat,
              longitude: lng,
            });
          }}
        />
      </Form.Item>
      <Form.Item
        name='city'
        label={<FormattedMessage {...Messages.labelCity} />}
        rules={[
          {
            required: true,
            message: 'City is required',
          },
        ]}
      >
        <CityAutocomplete
          placeholder={intl.formatMessage(Messages.placeHolderCity)}
          onCityStateSelect={({ state }) => {
            formInstance.setFieldsValue({ state });
          }}
          country={country}
          types={['(cities)']}
        />
      </Form.Item>
      <Form.Item
        name='state'
        label={<FormattedMessage {...Messages.labelState} />}
      >
        <StateAutocomplete
          placeholder={intl.formatMessage(Messages.placeHolderState)}
          country={country}
        />
      </Form.Item>
      <Form.Item
        name='country'
        label={<FormattedMessage {...Messages.labelCountry} />}
      >
        <FlagCountrySelectionFieldInput
          searchable={true}
          className='company-location-form__country-input'
          placeholder=''
          onSelect={(value) => {
            formInstance.setFieldsValue({
              ...formInstance.getFieldsValue(),
              country: CountryCode[value],
            });
          }}
          ref={refCountry}
          defaultCountry='US'
        />
      </Form.Item>
      <Form.Item
        name='zipcode'
        label='Zipcode'
        rules={[
          () => ({
            validator(rule, value) {
              if (!value) {
                return Promise.resolve();
              }
              //* Generic postal code: letters/digits with optional single
              //* space or hyphen, 3-10 chars (supports US, CA, and others)
              const postalCode = /^[A-Za-z0-9][A-Za-z0-9\s-]{1,8}[A-Za-z0-9]$/;
              if (postalCode.test(value)) {
                return Promise.resolve();
              } else {
                return Promise.reject('Invalid Zipcode');
              }
            },
          }),
        ]}
      >
        <Input
          name='zip'
          placeholder={intl.formatMessage(Messages.placeHolderZipCode)}
        />
      </Form.Item>
      <Form.Item
        name='locationName'
        label={<FormattedMessage {...Messages.labelLocationName} />}
        rules={[
          {
            required: true,
            message: 'Location Name is required',
          },
        ]}
      >
        <Input
          name='locationName'
          placeholder={intl.formatMessage(Messages.placeHolderLocationName)}
        />
      </Form.Item>
      <Form.Item
        name='locationDescription'
        label={<FormattedMessage {...Messages.labelLocationDescription} />}
      >
        <TextArea
          showCount={false}
          maxLength={300}
          placeholder={intl.formatMessage(Messages.placeHolderLocationDes)}
          autoSize={{ minRows: 5, maxRows: 5 }}
        />
      </Form.Item>
      <Form.Item
        name='phoneNumber'
        label={
          <div>
            <FormattedMessage {...Messages.labelPhone} />
          </div>
        }
      >
        <Input
          name='phoneNumber'
          placeholder={intl.formatMessage(Messages.placeHolderPhone)}
        />
      </Form.Item>
      <Form.Item
        name='mobileNumber'
        label={
          <div>
            <FormattedMessage {...Messages.labelMobile} />
          </div>
        }
      >
        <Input
          name='mobileNumber'
          placeholder={intl.formatMessage(Messages.placeHolderMobile)}
        />
      </Form.Item>
      <Form.Item
        name='faxNumber'
        label={<FormattedMessage {...Messages.labelFax} />}
      >
        <Input
          name='faxNumber'
          placeholder={intl.formatMessage(Messages.placeHolderFax)}
        />
      </Form.Item>
      <Form.Item
        name='email'
        label={<FormattedMessage {...Messages.labelEmail} />}
        rules={[{ type: 'email', message: 'Invalid Email' }]}
      >
        <Input
          name='email'
          placeholder={intl.formatMessage(Messages.placeHolderEmail)}
        />
      </Form.Item>
      <Form.Item name='latitude' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='longitude' hidden>
        <Input />
      </Form.Item>

      {/* BP 12/28/2020 - remove latitude and longitude fields
  {isAddNew && (
    <>
      <Form.Item
        name='latitude'
        label='Latitude'
        rules={[
          () => ({
            validator(rule, value) {
              const regex = /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/;
              if (regex.exec(value)) {
                return Promise.resolve();
              } else {
                return Promise.reject('Invalid Latitude');
              }
            },
          }),
        ]}
      >
        <Input
          name='latitude'
          label={<FormattedMessage {...Messages.labelLatitude} />}
          placeholder={intl.formatMessage(
            Messages.placeHolderLatitude
          )}
        />
      </Form.Item>
      <Form.Item
        name='longitude'
        label={<FormattedMessage {...Messages.labelLongitude} />}
        rules={[
          () => ({
            validator(rule, value) {
              const regex = /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/;
              if (regex.exec(value)) {
                return Promise.resolve();
              } else {
                return Promise.reject('Invalid Longitude');
              }
            },
          }),
        ]}
      >
        <Input
          name='longitude'
          placeholder={intl.formatMessage(
            Messages.placeHolderLongitude
          )}
        />
      </Form.Item>
    </>
  )} */}
    </Form>
  );
});

CompanyLocationForm.propTypes = {
  isOpen: PropTypes.bool,
  isAddNew: PropTypes.bool,
  setIsOpen: PropTypes.func,
  itemData: PropTypes.object,
};

export default injectIntl(CompanyLocationForm);
